import { Button } from 'app/components/ui/button'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { RadioGroup } from 'app/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { formatPhoneWithLabel } from 'app/formatPhoneNumber'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import type { IvrConfig, IvrNode } from 'app/types/ivr'
import type { UseIvrBuilderReturn } from './useIvrBuilder'
import type { IvrFormState } from './ivr-form'
import { AUDIO_TYPE_OPTIONS, DEFAULT_RING_COUNT, parseConfigToForm } from './ivr-form'
import { AudioInput } from './AudioInput'
import { IvrNodeTree } from './IvrNodeTree'
import { IvrNodeEditor } from './IvrNodeEditor'
import { useAudioUpload } from './useAudioUpload'

// ─── Main Component ──────────────────────────────────────────────────────────

export type IvrBuilderComponentProps = UseIvrBuilderReturn

export const IvrBuilderComponent = observer(function IvrBuilderComponent(props: IvrBuilderComponentProps) {
  const {
    location,
    ivrNumbers,
    hasIvrNumbers,
    selectedIvrId,
    setSelectedIvrId,
    config,
    configLoaded,
    setConfig,
    save,
    saving: savingConfig,
  } = props
  const [confirm] = useConfirmation()

  const [form, setForm] = useState<IvrFormState>(() => parseConfigToForm(config))
  const [savedForm, setSavedForm] = useState<IvrFormState>(() => parseConfigToForm(config))
  const [savedConfig, setSavedConfig] = useState<IvrConfig>(config)
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(config?.root)
  const [uploadingAudio, setUploadingAudio] = useState(false)
  const [activePlayerId, setActivePlayerId] = useState<string | undefined>()
  const pendingFiles = useRef<Map<string, File>>(new Map())
  const isLocalEdit = useRef(false)
  const { upload } = useAudioUpload(location?.id || ``)

  const saving = savingConfig || uploadingAudio
  const isDirty =
    JSON.stringify(form) !== JSON.stringify(savedForm) ||
    JSON.stringify(config) !== JSON.stringify(savedConfig) ||
    pendingFiles.current.size > 0

  const hasConfig = config?.nodes && Object.keys(config.nodes).length > 0

  // The effective root for UI purposes — skip the businessHours wrapper
  const effectiveRootId = (() => {
    const rootNode = config?.nodes?.[config.root]
    if (rootNode?.type === `businessHours`) return (rootNode as { duringHours: string }).duringHours
    return config.root
  })()

  // Sync form state when config is loaded/reloaded from server (not from local edits)
  useEffect(() => {
    if (isLocalEdit.current) {
      isLocalEdit.current = false
      return
    }
    const parsed = parseConfigToForm(config)
    setForm(parsed)
    setSavedForm(parsed)
    setSavedConfig(config)
    if (config?.root) setSelectedNodeId(config.root)
  }, [config])

  // Auto-scaffold a greeting (menu) node when no config exists
  useEffect(() => {
    if (!configLoaded || hasConfig || !location || ivrNumbers.length === 0) return
    isLocalEdit.current = true
    const menuId = `node-${Date.now()}`
    setConfig({
      enabled: true,
      root: menuId,
      nodes: {
        [menuId]: { type: `menu`, audio: { type: `tts`, text: `` }, options: [], timeout: 10, noInput: `replay` },
      },
    })
    setSelectedNodeId(menuId)
    setForm((prev) => ({ ...prev, enabled: true, businessHoursEnabled: false, afterHoursMessage: `` }))
  }, [configLoaded, hasConfig, location, ivrNumbers.length])

  if (!location) return <div className="p-6">Loading...</div>
  if (!ivrNumbers.length)
    return (
      <div className="p-6">
        {hasIvrNumbers
          ? `All IVR numbers for this location are already assigned to an IVR.`
          : `No IVR numbers configured for this location.`}
      </div>
    )

  const updateForm = (partial: Partial<IvrFormState>) => setForm((prev) => ({ ...prev, ...partial }))

  const handleNodeChange = (nodeId: string, updated: Partial<IvrNode>) => {
    isLocalEdit.current = true
    setConfig((prev) => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [nodeId]: { ...prev.nodes[nodeId], ...updated } as IvrNode,
      },
    }))
  }

  const replaceNode = (nodeId: string, node: IvrNode) => {
    isLocalEdit.current = true
    setConfig((prev) => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [nodeId]: node,
      },
    }))
  }

  const removeNode = (nodeId: string) => {
    isLocalEdit.current = true
    setConfig((prev) => {
      // Collect the full subtree reachable from the deleted node
      const toRemove = new Set<string>()
      const collectSubtree = (id: string) => {
        if (!id || toRemove.has(id)) return
        toRemove.add(id)
        const node = prev.nodes[id]
        if (!node) return
        if (node.type === `menu` && node.options) {
          node.options.forEach((o) => collectSubtree(o.targetNodeId))
        }
        if (node.type === `dial` && node.noAnswer) collectSubtree(node.noAnswer)
        if (node.type === `greeting` && node.next) collectSubtree(node.next)
        if (node.type === `announcement` && node.next) collectSubtree(node.next)
        if (node.type === `businessHours`) {
          if (node.duringHours) collectSubtree(node.duringHours)
          if (node.afterHours) collectSubtree(node.afterHours)
        }
      }
      collectSubtree(nodeId)

      // Remove all collected nodes
      const remainingNodes = { ...prev.nodes }
      for (const id of toRemove) {
        delete remainingNodes[id]
      }

      // Clean up references to the deleted node from remaining parent nodes
      for (const [id, node] of Object.entries(remainingNodes) as [string, IvrNode][]) {
        if (node.type === `menu` && node.options) {
          const filtered = node.options.filter((o) => !toRemove.has(o.targetNodeId))
          if (filtered.length !== node.options.length) {
            remainingNodes[id] = { ...node, options: filtered }
          }
        }
        if (node.type === `dial` && node.noAnswer && toRemove.has(node.noAnswer)) {
          const { noAnswer: __, ...rest } = node
          remainingNodes[id] = rest as IvrNode
        }
        if (node.type === `greeting` && node.next && toRemove.has(node.next)) {
          remainingNodes[id] = { ...node, next: `` }
        }
        if (node.type === `announcement` && node.next && toRemove.has(node.next)) {
          const { next: __, ...rest } = node
          remainingNodes[id] = rest as IvrNode
        }
      }
      return { ...prev, nodes: remainingNodes }
    })
    setSelectedNodeId(effectiveRootId)
  }

  const addMenuOption = (menuNodeId: string, nodeType: `dial` | `voicemail` | `menu`) => {
    isLocalEdit.current = true
    const newId = `node-${Date.now()}`
    setConfig((prev) => {
      const menuNode = prev.nodes[menuNodeId]
      if (menuNode?.type !== `menu`) return prev
      const options = menuNode.options || []

      // Find first available DTMF digit (1-9)
      const usedDigits = new Set(options.map((o) => o.digit))
      let nextDigit: string | undefined
      for (let d = 1; d <= 9; d++) {
        if (!usedDigits.has(String(d))) {
          nextDigit = String(d)
          break
        }
      }
      if (!nextDigit) return prev // All 9 digits taken

      let newNode: IvrNode
      if (nodeType === `dial`) {
        const vmId = `node-${Date.now() + 1}`
        return {
          ...prev,
          nodes: {
            ...prev.nodes,
            [newId]: { type: `dial`, target: ``, ringCount: DEFAULT_RING_COUNT, noAnswer: vmId },
            [vmId]: { type: `voicemail`, email: `` },
            [menuNodeId]: { ...menuNode, options: [...options, { digit: nextDigit, targetNodeId: newId }] },
          },
        }
      } else if (nodeType === `menu`) {
        newNode = { type: `menu`, audio: { type: `tts`, text: `` }, options: [], timeout: 10, noInput: `replay` }
      } else {
        newNode = { type: `voicemail`, email: `` }
      }

      return {
        ...prev,
        nodes: {
          ...prev.nodes,
          [newId]: newNode,
          [menuNodeId]: { ...menuNode, options: [...options, { digit: nextDigit, targetNodeId: newId }] },
        },
      }
    })
    setSelectedNodeId(newId)
  }

  const handleSave = async () => {
    // Phase 1: Upload pending audio files, accumulate results locally
    const uploadedKeys = new Map<string, string>()
    if (pendingFiles.current.size > 0) {
      setUploadingAudio(true)
      for (const [slot, file] of pendingFiles.current.entries()) {
        const key = await upload(file)
        if (!key) {
          setUploadingAudio(false)
          return // Abort save on upload failure (useAudioUpload already shows error toast)
        }
        uploadedKeys.set(slot, key)
      }
      setUploadingAudio(false)
    }

    pendingFiles.current.clear()

    // Phase 2: Build the final config from current state
    let finalConfig = { ...config, enabled: form.enabled }

    // Apply uploaded audio keys directly to finalConfig (avoids React state race)
    for (const [slot, key] of uploadedKeys) {
      if (slot === `after-hours`) {
        // Will be applied below in the businessHours section
      } else if (finalConfig.nodes[slot]) {
        finalConfig = {
          ...finalConfig,
          nodes: {
            ...finalConfig.nodes,
            [slot]: { ...finalConfig.nodes[slot], audio: { type: `s3`, key } } as IvrNode,
          },
        }
      }
    }

    // Resolve after-hours audio (prefer freshly uploaded key)
    const afterHoursKey = uploadedKeys.get(`after-hours`)
    const afterHoursAudio = afterHoursKey
      ? { type: `s3` as const, key: afterHoursKey }
      : form.afterHoursAudioType === `tts`
        ? { type: `tts` as const, text: form.afterHoursMessage }
        : { type: `s3` as const, key: form.afterHoursAudioKey }

    if (form.businessHoursEnabled) {
      const hasExistingBh = config.nodes[config.root]?.type === `businessHours`
      if (!hasExistingBh) {
        const afterId = `after-hours-msg`
        const bhId = `bh-root`
        finalConfig = {
          ...finalConfig,
          root: bhId,
          nodes: {
            ...finalConfig.nodes,
            [afterId]: { type: `voicemail`, audio: afterHoursAudio, email: `` },
            [bhId]: { type: `businessHours`, duringHours: config.root, afterHours: afterId },
          },
        }
      } else {
        const bhNode = config.nodes[config.root] as { type: 'businessHours'; afterHours: string; duringHours: string }
        if (bhNode.afterHours && finalConfig.nodes[bhNode.afterHours]) {
          finalConfig = {
            ...finalConfig,
            nodes: {
              ...finalConfig.nodes,
              [bhNode.afterHours]: { type: `voicemail`, audio: afterHoursAudio, email: `` },
            },
          }
        }
      }
    } else {
      // Remove businessHours wrapper if it was previously enabled
      const rootNode = config.nodes[config.root]
      if (rootNode?.type === `businessHours`) {
        const bhNode = rootNode as { type: 'businessHours'; duringHours: string; afterHours: string }
        const { [config.root]: _, [bhNode.afterHours]: __, ...remainingNodes } = finalConfig.nodes
        finalConfig = { ...finalConfig, root: bhNode.duringHours, nodes: remainingNodes }
      }
    }

    // Phase 3: Persist — only update saved snapshots on confirmed success
    isLocalEdit.current = true
    setConfig(finalConfig)
    const success = await save(finalConfig)
    if (success) {
      setSavedForm(form)
      setSavedConfig(finalConfig)
    }
  }

  const handleDiscard = () => {
    setForm(savedForm)
    setConfig(savedConfig)
    pendingFiles.current.clear()
  }

  return (
    <div className="col-span-6 flex flex-col gap-4">
      {/* Header: IVR Configuration + Business Hours */}
      <div className="rounded-lg border border-default bg-surface px-5 py-4 shadow-elevation-2">
        <div className="flex items-center justify-between">
          <h3 className="text-heading-xs text-strong">IVR Configuration</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-ui-xs text-default">IVR Number</span>
              <Select value={selectedIvrId ?? ``} onValueChange={setSelectedIvrId}>
                <SelectTrigger className="w-auto min-w-64 max-w-xs text-body-sm">
                  <SelectValue placeholder="Select an IVR number" />
                </SelectTrigger>
                <SelectContent>
                  {ivrNumbers.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {formatPhoneWithLabel(n.phone, n.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.businessHoursEnabled}
                onCheckedChange={(checked) => updateForm({ businessHoursEnabled: checked })}
                disabled={!form.enabled}
              />
              <span className="text-ui-xs text-default">Business Hours</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => {
                  updateForm({ enabled: checked })
                }}
              />
              <span className="text-ui-xs text-default">Enabled</span>
            </div>
          </div>
        </div>

        {/* After-hours message (shown when business hours enabled) */}
        {form.businessHoursEnabled && form.enabled && (
          <div className="mt-4 border-t border-default pt-4">
            <Label className="text-body-xs font-medium text-default">After-Hours Message</Label>
            <p className="mt-0.5 text-body-xs text-minimal">
              Callers outside Location business hours hear this instead of the IVR menu.
            </p>
            <div className="mt-2">
              <AudioInput
                audioType={form.afterHoursAudioType}
                text={form.afterHoursMessage}
                audioKey={form.afterHoursAudioKey}
                id="after-hours"
                activePlayerId={activePlayerId}
                onTextChange={(val) => updateForm({ afterHoursMessage: val })}
                onKeyChange={(val) => updateForm({ afterHoursAudioKey: val })}
                onFileSelect={(file) => {
                  if (file) pendingFiles.current.set(`after-hours`, file)
                  else pendingFiles.current.delete(`after-hours`)
                }}
                onPlay={setActivePlayerId}
                placeholder="We are currently closed. Please call back during business hours."
              />
              <RadioGroup
                className="mt-2"
                values={AUDIO_TYPE_OPTIONS}
                value={form.afterHoursAudioType}
                onChange={(val) => updateForm({ afterHoursAudioType: val as `tts` | `s3` })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Two-column: Tree + Editor */}
      {hasConfig ? (
        <div
          className={`flex overflow-hidden rounded-lg border border-default shadow-elevation-2 ${!form.enabled ? `pointer-events-none opacity-50` : ``}`}
          style={{ minHeight: `480px` }}
        >
          <IvrNodeTree config={config} selectedNodeId={selectedNodeId} onSelectNode={setSelectedNodeId} />
          <IvrNodeEditor
            config={config}
            nodeId={selectedNodeId || config.root}
            rootId={effectiveRootId}
            activePlayerId={activePlayerId}
            onNodeChange={handleNodeChange}
            onReplaceNode={replaceNode}
            onRemoveNode={removeNode}
            onAddMenuOption={addMenuOption}
            onPlay={setActivePlayerId}
            onFileSelect={(slot, file) => {
              if (file) pendingFiles.current.set(slot, file)
              else pendingFiles.current.delete(slot)
            }}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-default bg-surface-subtle py-16 shadow-elevation-2">
          <p className="text-body-sm text-subtle">No call flow configured yet.</p>
          <p className="mt-1 text-body-xs text-minimal">Enable the IVR to start building your call flow.</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between">
        {hasConfig && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              confirm({
                title: `Delete IVR Configuration`,
                description: `This will permanently delete the entire call flow. Callers will be forwarded directly without an IVR menu.`,
                variant: `danger`,
              }).then(() => {
                isLocalEdit.current = true
                setConfig({ enabled: false, root: ``, nodes: {} })
                setSelectedNodeId(undefined)
                updateForm({ enabled: false })
                save({ enabled: false, root: ``, nodes: {} })
              })
            }}
            disabled={saving}
          >
            Delete Configuration
          </Button>
        )}
        <div className="flex gap-3 ml-auto">
          <Button variant="tertiary" size="sm" onClick={handleDiscard} disabled={saving || !isDirty}>
            Discard Changes
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !isDirty}>
            {saving ? `Saving...` : `Save Configuration`}
          </Button>
        </div>
      </div>
    </div>
  )
})
